/**
 * @module app.cm
 */

import { autoinject } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import * as CM from 'app/cm/models/essette-api';


@autoinject
export class SystemReviewService {

    constructor(protected api: Api) { }

    ////****************** Queries **************************************************////
    public async loadSystems(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.System>> {
        const query = this.api
            .query(`cm/systems`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadIllnessesForSystem(systemId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.SystemReviewIllness>> {
        const query = this.api
            .query(`cm/systems/${systemId}/illnesses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSystemIllnesses(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.SystemIllness>> {
        const query = this.api
            .query(`cm/systems/illnesses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadOnsetDefinitions(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.IllnessOnsetDefinition>> {
        const query = this.api
            .query(`cm/systems/onsets/definitions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadSystemReviews(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.SystemReview>> {
        const query = this.api
            .query(`cm/systems/reviews`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadSystemReviewIllnesses(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.SystemReviewIllness>> {
        const query = this.api
            .query(`cm/systems/reviews/illnesses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    /**
     * Returns CM.SystemReviewIlnesses for an assessment session and question
     * @param systemReviewId This is an AssessmentSessionID
     * @param assessmentQuestionId
     */
    public async loadSystemReviewQuestionIllnesses(systemReviewId: string, assessmentQuestionId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.SystemReviewIllness>> {
        const query = this.api
            .query(`cm/systems/reviews/${systemReviewId}/illnesses`)
            .filterIdentifier("assessment.question.id", assessmentQuestionId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    /**
     * Note: Returns CM.SystemReview. Does not return illnesses for a System Review. For Illnesses, use loadSystemReviewQuestionIllnesses()
     * @param systemReviewId This is an AssessmentSessionID
     */
    public async loadSystemReview(systemReviewId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.SystemReview>> {
        const query = this.api
            .query(`cm/systems/reviews/${systemReviewId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSystemReviewIllness(systemReviewIllnessId: string, includes: string[] = []): Promise<IApiQueryResults<CM.SystemReviewIllness>> {
        const query = this.api
            .query(`cm/systems/reviews/illnesses/${systemReviewIllnessId}`)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////
    public async insertSystemReviewIllness(systemReviewIllness: CM.SystemReviewIllness): Promise<IApiActionResults> {
        const action = this.api.action("insert", "cm/illnesses", systemReviewIllness);

        return await this.api.post(action);
    }

    public async saveSystemIllness(illness: CM.SystemIllness): Promise<IApiActionResults> {
        const actionName = !illness.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cm/systems/illnesses/${illness.id ? `${illness.id}` : ``}`, illness);

        return await this.api.post(action);
    }

    public async activateSystemIllness(systemIllnessId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `cm/Systems/illnesses/${systemIllnessId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async insertSystemReview(systemReview: CM.SystemReview): Promise<IApiActionResults> {
        const action = this.api.action("insert", "cm/systems/reviews", systemReview);

        return await this.api.post(action);
    }

    public async saveSystem(system: CM.System): Promise<IApiActionResults> {
        const actionName = !system.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `/cm/systems/${system.id ? `${system.id}` : ``}`, system);

        return await this.api.post(action);
    }

    public async activateSystem(systemId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `/cm/systems/${systemId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }
}
